import { alpha, Avatar, Box, Card, Chip, Grid, Rating, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import Title3 from '../../components/title/title3';
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LineChart } from '@mui/x-charts/LineChart';
import Zoom from '@mui/material/Zoom';
import CustomPagination from "../../components/pagination/customPagination";
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';

const platformLocales = "platforms";
const ITEMS_PER_PAGE = 8;

const getLinkIcon = (type) => {
    if(type === "WEBSITE") return "/images/platforms/Icons/web.svg";
    if(type === "PLAY_STORE") return "/images/platforms/Icons/playstore.svg";
    if(type === "APP_STORE") return "/images/platforms/Icons/appstore.svg";
    
    return "";
}

const getReviewPlatformThumbnail = (type) => {
    if(type === "GOOGLE") return '/images/platforms/Reviews/Google.png';
    if(type === "TRIP_ADVISOR") return '/images/platforms/Reviews/Trip Advisor.png';
    if(type === "TRUST_PILOT") return '/images/platforms/Reviews/Trust Pilot.png';
    if(type === "PLAY_STORE") return "/images/platforms/Reviews/playstore.png";
    
    return "";
}

const PlatformDetailsView = ({
    file
}) => {
    const theme = useTheme();
    const {t} = useTranslation();

    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        import(`../../data/platforms/en/${file}.json`)
        .then((module) => setData(module.default))
        .catch((err) => setError(true));
    }, [file]);

    const [activePage, setActivePage] = useState(0);
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    const changePage = useCallback((page) => {
        setActivePage(page);
    }, []);

    return (
        <>
            <Stack
                direction='column'
                spacing={{xs: 3, md: 5}}
            >
                {/* Title */}
                <Grid
                    direction='row'
                    alignItems='end'
                    container
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        p: {xs: 2.5, md: 5},
                        borderRadius: 4
                    }}
                >
                    <Grid
                        size={{xs: 12, lg: 6}}
                        direction='column'
                        alignItems='center'
                    >
                        <Title3 
                            title={data?.name}
                            description={data?.shortDescription}
                            removePb
                        />

                        {/* <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent={{xs: 'center', md: 'start'}}
                            spacing={1}
                            sx={{
                                pt: 1,
                                width: '100%',
                                display: Number.parseFloat(data?.rating) > 0 ? 'flex' : 'none'
                            }}
                        >
                            <Rating 
                                name="read-only" 
                                value={Number.parseFloat(data?.rating)} 
                                readOnly
                                precision={0.5}
                                size="small"
                            />

                            <Typography
                                fontSize={12}
                                color={theme.palette.text.secondary}
                                fontWeight={600}
                            >
                                {`${data?.rating} (${data?.reviews} reviews)`}
                            </Typography>
                        </Stack> */}
                    </Grid>

                    <Grid
                        size={{xs: 12, lg: 6}}
                        display={data?.link ? 'flex' : 'none'}
                        direction='row'
                        justifyContent={{xs: 'center', lg: 'end'}}
                        pt={{xs: data?.link ? 3 : 0, lg: 0}}
                        gap="8px"
                    >
                        {
                            data?.link.map((link, index) => {
                                return <Link
                                    key={index}
                                    to={link.src}
                                    target="_blank"
                                    style={{
                                        textDecoration: 'none'
                                    }}
                                >
                                    <Chip
                                        label={t(`${platformLocales}.visit`)}
                                        onClick={()=>{}}
                                        icon={
                                            <Avatar 
                                                alt="Icon" 
                                                src={getLinkIcon(link.type)}
                                                sx={{
                                                    width: '16px',
                                                    height: '16px'
                                                }}
                                            />
                                        }
                                        sx={{
                                            backgroundColor: data?.theme ?? 'black',
                                            color: 'white',
                                            px: {xs: 3, md: 5},
                                            '&: hover': {
                                                backgroundColor: data?.theme ?? 'black',
                                                color: 'white',
                                                transform: 'scale(0.9)'
                                            }
                                        }}
                                    />
                                </Link>
                            })
                        }
                    </Grid>
                </Grid>

                {/* Story */}
                <Grid
                    direction={{xs: 'column-reverse', md: 'row'}}
                    justifyContent={{xs: 'center', md: 'start'}}
                    alignItems={{xs: 'start', md: 'center'}}
                    container
                    spacing={{xs: 2.5, md: 5}}
                >
                    <Grid
                        size={{xs: 12, md: 7}}
                        direction='column'
                    >
                        <SubRegion 
                            title={t(`${platformLocales}.storyTitle`)}
                            body={
                                <>
                                    <Typography 
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: {xs: 12, md: 14},
                                            textAlign: "justify",
                                            lineHeight: {xs: 1.5, md: 1.75},
                                            color: theme.palette.text.secondary
                                        }}
                                    >
                                        {data?.successStory}
                                    </Typography>
                                </>
                            }
                        />
                    </Grid>

                    <Grid
                        size={{xs: 12, md: 5}}
                    >
                        <img 
                            src={data?.thumbnail} 
                            alt="Thumbnail" 
                            width='100%' 
                        />
                    </Grid>
                </Grid>

                {/* Timeline */}
                <SubRegion 
                    title={t(`${platformLocales}.timeline`)}
                    body={
                        <Stack 
                            direction='row'
                            justifyContent='start'
                            sx={{ 
                                width: '100%' 
                            }}
                        >
                            <GrowthTimeline 
                                timelineList={data?.timeline ?? []}
                                themeColor={data?.theme}
                            />
                        </Stack>
                    }
                />

                {/* Reviews */}
                <SubRegion 
                    title={t(`${platformLocales}.rating`)}
                    body={
                        <Box sx={{ width: '100%' }}>
                            <Grid
                                container
                                spacing={{xs: 2, md: 4}}
                            >
                                {
                                    data?.ratings?.map((platform, index) => {
                                        return <Grid
                                            key={index}
                                            size={{xs: 12, lg: 4}}
                                        >
                                            <ReviewCard 
                                                themeColor={data?.theme}
                                                img={getReviewPlatformThumbnail(platform.type)}
                                                rating={platform.rating}
                                                reviews={platform.reviews}
                                                platform={platform.type}
                                                link={platform.src}
                                            />
                                        </Grid>
                                    })
                                }
                            </Grid>
                        </Box>
                    }
                />

                {/* Growth */}
                {/* <SubRegion 
                    title={t(`${platformLocales}.growthMap`)}
                    body={
                        <Box sx={{ width: '100%', height: {xs: 200, md: 300} }}>
                            <LineChart
                                series={[
                                    { 
                                        data: data?.growthMap?.yAxisValues ?? [], 
                                        label: data?.growthMap?.label, 
                                        yAxisId: 'leftAxisId',
                                        color:  data?.theme ?? theme.palette.primary.main
                                    }
                                ]}
                                xAxis={[
                                    { 
                                        scaleType: 'point', 
                                        data: data?.growthMap?.xAxis ?? [] 
                                    }
                                ]}
                                yAxis={[
                                    { id: 'leftAxisId', width: 30 }
                                ]}
                                
                            />
                        </Box>
                    }
                /> */}

                {/* Countries */}
                {/* <SubRegion 
                    title={t(`${platformLocales}.reach`)}
                    body={
                        <>
                            <Grid
                                direction='row'
                                container
                            >
                                {
                                    data?.countries.map((item, index) => {
                                        return <Grid
                                            key={index}
                                            size={{xs: 2, md: 1}}
                                            p={{xs: 0.25, md: 0.5}}
                                            sx={{
                                                display: item.service ? "flex" : 'none'
                                            }}
                                        >
                                            <Tooltip 
                                                title={item.name} 
                                                arrow
                                                slots={{
                                                    transition: Zoom
                                                }}
                                                enterTouchDelay={0}
                                            >
                                                <img src={item.flag} alt={item.name} width='100%' />
                                            </Tooltip>
                                        </Grid>
                                    })
                                }
                            </Grid>
                        </>
                    }
                    hide={data?.countries.length === 0}
                /> */}

                {/* Chauffeurs */}
                {/* <SubRegion 
                    title={t(`${platformLocales}.chauffeurs`)}
                    hide={!data?.Chauffeurs || data?.Chauffeurs?.length === 0}
                    body={
                        <>
                            <Grid
                                direction='row'
                                container
                                spacing={{xs: 2.5, md: 5}}
                                pt={{xs: 1, md: 2.5}}
                            >
                                {
                                    data?.Chauffeurs?.slice(activePage * ITEMS_PER_PAGE, ITEMS_PER_PAGE * (1 + activePage))?.map((item, index) => {
                                        return <Grid
                                            key={index}
                                            size={{xs: 6, md: 4, lg: 3}}
                                            display='flex'
                                            direction='row'
                                            justifyContent='center'
                                            data-aos="zoom-in"
                                        >
                                            <Chauffeur data={item} themeColor={data?.theme}/>
                                        </Grid>
                                    })
                                }
                            </Grid>

                            <Stack
                                direction='row'
                                justifyContent='center'
                            >
                                <CustomPagination 
                                    totalItems={data?.Chauffeurs?.length}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    activePage={activePage}
                                    onPageChange={changePage}
                                />
                            </Stack>
                        </>
                    }
                /> */}
            </Stack>
        </>
    );
}

export default PlatformDetailsView;

const SubRegion = ({
    title,
    body,
    hide=false
}) => {
    return (
        <>
            <Stack
                direction='column'
                display={hide ? 'none' : 'flex'}
            >
                <SubHeading title={title} />

                {body}
            </Stack>
        </>
    );
}

const SubHeading = ({
    title
}) => {
    const theme = useTheme();

    return (
        <>
            <Typography 
                sx={{
                    fontWeight: 800,
                    fontSize: {xs: 12, md: 16},
                    textAlign: "left",
                    lineHeight: 1.5,
                    color: theme.palette.text.primary,
                    pb: {xs: 1, md: 2}
                }}
                data-aos="slide-up"
            >
                {title}
            </Typography>
        </>
    );
}

const Chauffeur = ({
    data,
    themeColor
}) => {
    const theme = useTheme();

    return (
        <>
            <Stack
                direction='column'
                alignItems='center'
                sx={{
                    position: 'relative',
                    width: '100%',
                    pt: {xs: "40px", md: "75px"}
                }}
            >
                <Avatar 
                    alt={data.name}
                    src={data.image}
                    sx={{
                        position: 'absolute',
                        width: {xs: 75, md: 150},
                        height: {xs: 75, md: 150},
                        transform: {xs: 'translateY(-50px)', md: 'translateY(-75px)'},
                        border: `5px solid ${theme.palette.background.paper}`
                    }}
                />

                <Card
                    elevation={3}
                    sx={{
                        p: {xs: 0.25, md: 0.5},
                        width: '100%',
                        backgroundColor: theme.palette.background.default,
                        borderRadius: {xs: 2, md: 4},
                        minHeight: {xs: 150, md: 200}
                    }}
                >
                    <Link
                        to={data.link}
                        target="_blank"
                    >
                        <Stack
                            direction='column'
                            alignItems='center'
                            position='absolute'
                            bottom={{xs: 10, md: 15}}
                            left='5%'
                            right='5%'
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                p: {xs: 1, md: 2},
                                borderRadius: {xs: 2, md: 4},
                                '&: hover': {
                                    transform: 'scale(0.9)'
                                }
                            }}
                        >
                            <Typography
                                fontWeight={900}
                                fontSize={{xs: 12, md: 18}}
                                color={themeColor}
                                textAlign="center"
                            >
                                {data.name}
                            </Typography>

                            <Typography
                                fontWeight={400}
                                fontSize={{xs: 10, md: 12}}
                                color={theme.palette.text.secondary}
                                textAlign="center"
                            >
                                {data.driverType}
                            </Typography>

                            <Stack
                                direction={{xs: 'column', md: 'row'}}
                                alignItems='center'
                                justifyContent='center'
                                spacing={{xs: 0, md: 1}}
                                sx={{
                                    pt: 1,
                                    width: '100%',
                                    display: Number.parseFloat(data.rating) > 0 ? 'flex' : 'none'
                                }}
                            >
                                <Rating 
                                    name="read-only" 
                                    value={Number.parseFloat(data.rating)} 
                                    readOnly
                                    precision={0.5}
                                    sx={{
                                        fontSize: {xs: 10, md: 12}
                                    }}
                                />

                                <Typography
                                    fontSize={10}
                                    color={theme.palette.text.secondary}
                                    fontWeight={600}
                                >
                                    {`${data.rating} (${data.reviews} reviews)`}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Link>
                </Card>
            </Stack>
        </>
    );
}

const GrowthTimeline = ({
    timelineList,
    themeColor
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [timeLineData, setTimeLineData] = useState([]);

    useEffect(() => {
        const list = [];

        let timelineListLength = timelineList.length;
        timelineListLength = timelineListLength %2 === 0 ? timelineListLength : timelineListLength - 1;

        for(let i = 0; i < timelineListLength; i += 2){
            list.push({
                left: timelineList[i],
                right: timelineList[i + 1]
            });
        }

        if(timelineList.length %2 === 1){
            list.push({
                left: timelineList[timelineList.length - 1],
                right: null
            });
        }

        setTimeLineData(list);
    }, [timelineList]);

    return (
        <>
            <Timeline position={isMobile ? "right" : "alternate"}>
                {
                    timeLineData.map((item, index) => {
                        return <Box
                            key={index}
                        >
                            {
                                [item.left, item.right].map((tL, index) => {
                                    return tL
                                    ?
                                    <TimelineItem
                                        key={index}
                                        sx={{
                                            my: 1
                                        }}
                                    >
                                        <TimelineOppositeContent>
                                            <Chip 
                                                label={tL.year}
                                                sx={{
                                                    backgroundColor: themeColor,
                                                    color: 'white',
                                                    px: {xs: 0, md: 3},
                                                    fontWeight: 700,
                                                    fontSize: {xs: '12px', md: '16px'}
                                                }}
                                            />
                                        </TimelineOppositeContent>

                                        <TimelineSeparator>
                                            <TimelineDot sx={{
                                                backgroundColor: themeColor,
                                                border: `2.5px solid #e9e9e9`
                                            }} />
                                            <TimelineConnector />
                                        </TimelineSeparator>
                                        
                                        <TimelineContent>
                                            <Stack
                                                direction='column'
                                                spacing={{xs: 1, md: 2}}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: {xs: '12px', md: '16px'},
                                                        color: theme.palette.text.primary
                                                    }}
                                                >
                                                    {tL.title}
                                                </Typography>

                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        backgroundColor: theme.palette.background.default,
                                                        p: {xs: 1, md: 2},
                                                        boxShadow: `0px 0px 2px 0px rgba(0, 0, 0, 0.5)`,
                                                        borderRadius: '8px'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 400,
                                                            fontSize: {xs: '12px', md: '14px'},
                                                            color: theme.palette.text.secondary,
                                                            lineHeight: {xs: 1.5, md: 1.75}
                                                        }}
                                                    >
                                                        {tL.description}
                                                    </Typography>
                                                </Card>
                                            </Stack>
                                        </TimelineContent>
                                    </TimelineItem>
                                    :
                                    <Box key={index}/>
                                })
                            }
                        </Box>
                    })
                }
            </Timeline>
        </>
    );
}

const ReviewCard = ({
    themeColor,
    img,
    rating,
    reviews,
    platform,
    link
}) => {
    const theme = useTheme();

    const viewReviews = useCallback(() => {
        if(!link) return;

        window.open(link, '_blank');
    }, [link]);

    return (
        <>
            <Stack
                direction='column'
                sx={{
                    backgroundColor: alpha(themeColor ?? '#ffffff', 0.1),
                    borderRadius: {xs: 2, md: 4},
                    py: 1,
                    pl: {xs: 2.5, md: 5},
                    '&: hover': {
                        transform: link ? 'scale(0.95)' : 'scale(1)'
                    },
                    cursor: link ? 'pointer' : 'default'
                }}
                onClick={viewReviews}
            >
                <Box
                    sx={{
                        width: {xs: 100, md: 200}
                    }}
                >
                    <img src={img} alt={platform} width='100%' />
                </Box>

                <Stack 
                    direction='row'
                    alignItems='center'
                    pb={{xs: 2, md: 3}}
                    sx={{
                        mt: {xs: -1, md: -2}
                    }}
                    spacing='5px'
                >
                    <Rating 
                        value={Number.parseFloat(rating)} 
                        precision={0.5} 
                        readOnly
                    />

                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: {xs: '16px', md: '20px'},
                            color: theme.palette.text.primary
                        }}
                    >
                        {rating}
                    </Typography>

                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: {xs: '16px', md: '20px'},
                            color: theme.palette.text.secondary
                        }}
                    >
                        {`(${reviews})`}
                    </Typography>
                </Stack>
            </Stack>
        </>
    );
}